import { Socket } from 'engine.io-client';

import teamSwitcherService from '../../team/teamSwitcher.service';
import type { ReceiveSocketEventBasePayload, SendSocketEventBasePayload } from './socket.types';
import type { SendSocketAuthEventPayloadMap } from './socketEvent.types';

export class SocketHandler<
  SendPayload extends SendSocketEventBasePayload,
  ReceivePayload extends ReceiveSocketEventBasePayload,
> {
  private retryTimeout?: NodeJS.Timeout;
  private socket?: Socket;
  private handleMessage: (payload: ReceivePayload) => Promise<void>;
  private teamId?: string;

  constructor(handleMessage: (payload: ReceivePayload) => Promise<void>) {
    this.handleMessage = handleMessage.bind(this);
    this.teamId = teamSwitcherService.getCurrentTeam()?.id;
  }

  open() {
    // Socket exists, so don't override it or open another.
    if (this.socket && ['open', 'opening'].includes(this.socket.readyState)) {
      return;
    }

    this.socket = new Socket(window.env.REACT_APP_WS_ENDPOINT, {
      transports: ['websocket'],
      path: window.env.REACT_APP_WS_PREFIX + '/sockets/v4',
      query: this.teamId ? { teamId: this.teamId } : undefined,
    });

    this.socket.on('open', () => {
      // eslint-disable-next-line no-console
      console.info('Socket connected', {
        state: this.socket?.readyState,
        socketId: this.socket?.id,
        teamId: this.teamId,
      });

      this.socket?.on('message', async (message) => {
        try {
          const payload = JSON.parse(message);
          await this.handleMessage(payload);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('socket on handle message error', error);
          this.socket?.close();
        }
      });
    });

    this.socket.on('close', (reason, description) => {
      // eslint-disable-next-line no-console
      console.error('Socket connection closed', { socketId: this.socket?.id, reason, description });
      const shouldRetry = ['transport close', 'transport error', 'ping timeout'].includes(reason);

      // eslint-disable-next-line no-console
      console.info(shouldRetry ? 'Socket disconnected, retrying...' : 'Socket connection closed', {
        reason,
        description,
        state: this.socket?.readyState,
        socketId: this.socket?.id,
      });

      this.socket = undefined;

      if (shouldRetry) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = setTimeout(() => {
          this.open();
        }, 2000);
      }
    });
  }

  close() {
    this.socket?.close();
  }

  send(payload: SendPayload | SendSocketAuthEventPayloadMap[keyof SendSocketAuthEventPayloadMap]) {
    if (!this.socket) {
      // eslint-disable-next-line no-console
      console.error('Attempted to send socket message when socket is not instantiated');
      return;
    }

    if (this.socket.readyState !== 'open') {
      // eslint-disable-next-line no-console
      console.warn('Attempted to send socket message when socket is not open', {
        state: this.socket.readyState,
        socketId: this.socket.id,
      });
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }
}
